import { yupResolver } from '@hookform/resolvers/yup';
import { Stack, useRouter } from 'expo-router';
import { useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useMutation, useQuery } from 'urql';
import * as Yup from 'yup';
import { Icon as AvatarIcon } from '@/components/Custom/Avatar';
import FormSaveButton from '@/components/Custom/FormSaveButton';
import ModalKeyboardStickyView from '@/components/Custom/ModalKeyboardStickyView';
import {
  AvatarColorPalette,
  AvatarPicker,
} from '@/components/FormFields/AvatarColorPicker';
import IdentityFields from '@/components/FormFields/IdentityFields';
import PersonEditorScrollView from '@/components/Person/PersonEditorScrollView';
import Box from '@/components/Unistyles/Box';
import { graphql } from '@/gql/graphql';
import useAvatarUploader from '@/hooks/useAvatarUploader';
import useUserId from '@/hooks/useUserId';
import getOffsetColor from '@/utils/getOffsetColor';
import getPersonDisplayName from '@/utils/getPersonDisplayName';
import palette from '@/utils/palette';

const DEFAULT_PROFILE_COLOR = palette[0];

type ProfileNameFormValues = {
  avatarId?: string | null;
  color: string;
  firstName?: string | null;
  lastName?: string | null;
  localAvatarUrl?: string | null;
  username: string;
};
const Schema: Yup.ObjectSchema<ProfileNameFormValues> = Yup.object().shape({
  username: Yup.string().required().min(3).max(20),
  color: Yup.string().required(),
  avatarId: Yup.string().nullable(),
  firstName: Yup.string().nullable().max(35),
  lastName: Yup.string().nullable().max(35),
  localAvatarUrl: Yup.string().nullable(),
});
const GetProfileName = graphql(/* GraphQL */ `
  query GetProfileName {
    current_user {
      id
      avatar_id
      color
      username
      first_name
      last_name
    }
  }
`);
const UpdateProfileName = graphql(`
  mutation UpdateProfileName(
    $id: String!
    $username: String!
    $firstName: String
    $lastName: String
    $color: String!
    $avatarId: uuid
  ) {
    update_person_by_pk(
      pk_columns: { id: $id }
      _set: {
        username: $username
        first_name: $firstName
        last_name: $lastName
        color: $color
        avatar_id: $avatarId
      }
    ) {
      id
      avatar_id
      color
      username
      first_name
      last_name
      name
    }
  }
`);
export default function ProfileName() {
  const id = useUserId();
  const { back } = useRouter();
  const { uploadAvatar } = useAvatarUploader();
  const [{ data }] = useQuery({
    query: GetProfileName,
  });
  const [, updateProfileName] = useMutation(UpdateProfileName);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const form = useForm<ProfileNameFormValues>({
    defaultValues: {
      avatarId: data?.current_user?.avatar_id,
      color: data?.current_user?.color || DEFAULT_PROFILE_COLOR,
      firstName: data?.current_user?.first_name,
      lastName: data?.current_user?.last_name,
      localAvatarUrl: null,
      username: data?.current_user?.username || '',
    },
    values: {
      avatarId: data?.current_user?.avatar_id,
      color: data?.current_user?.color || DEFAULT_PROFILE_COLOR,
      firstName: data?.current_user?.first_name,
      lastName: data?.current_user?.last_name,
      localAvatarUrl: null,
      username: data?.current_user?.username || '',
    },
    resolver: yupResolver(Schema),
  });
  const {
    control,
    setValue,
    handleSubmit,
    formState: { isDirty, isSubmitting, isValid },
  } = form;
  const watchedValues = useWatch({
    control,
  });
  const firstName = watchedValues.firstName ?? '';
  const lastName = watchedValues.lastName ?? '';
  const username = watchedValues.username ?? '';
  const displayName =
    getPersonDisplayName({ firstName, lastName, username }) || 'Profile';
  const submitForm = async (values: ProfileNameFormValues) => {
    setErrorMessage(null);
    let avatarId = values.avatarId || null;

    if (values.localAvatarUrl) {
      const uploadResult = await uploadAvatar({
        localUrl: values.localAvatarUrl,
        path: `user/${id}/profile`,
      });

      if (!uploadResult.ok) {
        setErrorMessage('Could not upload your profile photo.');
        return;
      }

      avatarId = uploadResult.avatarId;
    }

    const { error } = await updateProfileName({
      id,
      username: values.username?.trim() || '',
      firstName: values.firstName?.trim() || null,
      lastName: values.lastName?.trim() || null,
      color: values.color,
      avatarId,
    });
    if (!error) {
      back();
      return;
    }

    setErrorMessage(`${values.username} appears to be taken already.`);
  };

  return (
    <FormProvider {...form}>
      <Stack.Screen
        options={{
          title: 'Profile',
        }}
      />

      <PersonEditorScrollView
        name={displayName}
        avatarId={watchedValues.avatarId}
        color={watchedValues.color}
        masthead={
          <AvatarPicker
            avatarId={watchedValues.avatarId}
            avatarSize="3xl"
            nameSize="4xl"
            nameNumberOfLines={0}
            name={displayName}
            color={watchedValues.color}
            localAvatarUrl={watchedValues.localAvatarUrl}
            onChangeLocalAvatarUrl={(localAvatarUrl) =>
              setValue('localAvatarUrl', localAvatarUrl, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
          >
            <AvatarIcon
              name="camera-outline"
              size="5xl"
              color={getOffsetColor(watchedValues.color || '', false)}
            />
          </AvatarPicker>
        }
      >
        <Box p="md" space="md">
          <Box align="center" py="lg">
            <AvatarColorPalette
              color={watchedValues.color}
              onChangeColor={(color) =>
                setValue('color', color, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
            />
          </Box>
          <IdentityFields
            avatarContent={null}
            color={watchedValues.color || DEFAULT_PROFILE_COLOR}
            errorMessage={errorMessage}
            firstName={watchedValues.firstName || ''}
            lastName={watchedValues.lastName || ''}
            localAvatarUrl={watchedValues.localAvatarUrl}
            username={watchedValues.username || ''}
            onChangeColor={(value) =>
              setValue('color', value, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            onChangeFirstName={(value) =>
              setValue('firstName', value, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            onChangeLastName={(value) =>
              setValue('lastName', value, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            onChangeLocalAvatarUrl={(value) =>
              setValue('localAvatarUrl', value, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
            onChangeUsername={(value) =>
              setValue('username', value, {
                shouldDirty: true,
                shouldValidate: true,
              })
            }
          />
        </Box>
      </PersonEditorScrollView>
      <ModalKeyboardStickyView>
        <FormSaveButton
          info={{ text: 'Save' }}
          isDisabled={!isDirty || !isValid || isSubmitting}
          onPress={handleSubmit(submitForm)}
        />
      </ModalKeyboardStickyView>
    </FormProvider>
  );
}
